import { apiClient } from "./apiClient";
import { API_ENDPOINTS } from "../../src/constants";
import type { ApiResponse } from "@/types";

/* ---------- Types ---------- */
export interface Chatbot {
  id: string;
  code?: string;
  name: string;
  description?: string;
  isActive?: boolean;
  state?: string;
  metadata?: Record<string, any>;
  config?: Record<string, any>;
  agentInstanceIds?: string[];
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export type CreateChatbotDto = Omit<
  Chatbot,
  "id" | "createdAt" | "updatedAt"
> & { agentInstanceIds?: string[] };
export type UpdateChatbotDto = Partial<CreateChatbotDto>;

export type SyncTenantUserProfileDto = {
  chatbotId: string;
  tenantId: string;
  externalUserId: string;
  externalUserProfileId?: string;
  slug: string;
}


export interface ListParams {
  page?: number;
  limit?: number;
  state?: string;
  isActive?: boolean;
  includeDeleted?: boolean;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  search?: string;
  filter?: Record<string, any>;
  slug?: string;
  externalUserId?: string
  externalUserProfileId?: string
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

/* ---------- Helpers ---------- */
const buildListQuery = (params?: ListParams) => {
  const qs = new URLSearchParams();

  if (!params) return qs.toString();
  if (params.page !== undefined) qs.set("page", String(params.page));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.sortBy) qs.set("sortBy", params.sortBy);
  if (params.sortDir) qs.set("sortDir", params.sortDir);
  if (params.search) qs.set("search", params.search);
  if (params.filter && Object.keys(params.filter).length) {
    qs.set("filter", JSON.stringify(params.filter));
  }
  if (params.isActive !== undefined) qs.set("isActive", String(params.isActive));
  if (params.includeDeleted !== undefined) qs.set("includeDeleted", String(params.includeDeleted));
  if (params.slug) qs.set("slug", params.slug);
  if (params.externalUserId) qs.set("externalUserId", params.externalUserId);
  if (params.externalUserProfileId) qs.set("externalUserProfileId", params.externalUserProfileId);

  return qs.toString();
};

/* ================================================================
   Chatbot Service
================================================================ */
export const chatbotService = {
  async createChatbot(payload: CreateChatbotDto): Promise<Chatbot> {
    const result = await apiClient.post<ApiResponse<Chatbot>>(
      API_ENDPOINTS.CHATBOTS.CREATE,
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as Chatbot);
  },

  async syncTenantUserProfile(payload: SyncTenantUserProfileDto): Promise<any> {
    const result = await apiClient.post<ApiResponse<any>>(
      API_ENDPOINTS.CHAT.SYNC_TENANT_USER_PROFILE,
      payload,
      { authenticate: false }
    );
    return (result as any).data ?? (result as unknown as any);
  },

  async getChatbotById(id: string): Promise<Chatbot> {
    const result = await apiClient.get<ApiResponse<Chatbot>>(
      API_ENDPOINTS.CHATBOTS.GET_BY_ID(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as Chatbot);
  },

  async generateCodeScript(id: string): Promise<Chatbot> {
    const result = await apiClient.get<ApiResponse<Chatbot>>(
      API_ENDPOINTS.CHATBOTS.GENERATE_CODE_SCRIPT(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as Chatbot);
  },

  async listChatbots(params?: ListParams): Promise<Paginated<Chatbot>> {
    const query = buildListQuery(params);
    const url = query
      ? `${API_ENDPOINTS.CHATBOTS.LIST}?${query}`
      : API_ENDPOINTS.CHATBOTS.LIST;

    const result = await apiClient.get<ApiResponse<Paginated<Chatbot>>>(url, {
      authenticate: true,
    });
    return (result as any).data ?? (result as unknown as Paginated<Chatbot>);
  },

  async updateChatbot(id: string, payload: UpdateChatbotDto): Promise<Chatbot> {
    const result = await apiClient.patch<ApiResponse<Chatbot>>(
      API_ENDPOINTS.CHATBOTS.UPDATE(encodeURIComponent(id)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as Chatbot);
  },

  async deleteChatbot(id: string): Promise<{ success: boolean }> {
    const result = await apiClient.delete<ApiResponse<{ success: boolean }>>(
      API_ENDPOINTS.CHATBOTS.DELETE(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as { success: boolean });
  },

  async updateChatbotThemeSetting(id: string, payload: UpdateChatbotDto): Promise<Chatbot> {
    const result = await apiClient.put<ApiResponse<Chatbot>>(
      API_ENDPOINTS.CHATBOTS.UPDATETHEME(encodeURIComponent(id)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as Chatbot);
  },

  async restoreChatbot(id: string): Promise<{ success: boolean }> {
    const result = await apiClient.post<ApiResponse<{ success: boolean }>>(
      API_ENDPOINTS.CHATBOTS.RESTORE(encodeURIComponent(id)),
      {},
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as { success: boolean });
  },

  async listAgentInstanceDocuments(
    id: string,
    params?: ListParams
  ): Promise<Paginated<any>> {
    const query = buildListQuery(params);
    const path = `/core/agent-instances/${encodeURIComponent(id)}/documents`;
    const url = query ? `${path}?${query}` : path;

    const result = await apiClient.get<ApiResponse<Paginated<any>>>(url, {
      authenticate: true,
    });

    return (result as any).data ?? (result as unknown as Paginated<any>);
  },

  async listConversationHistory(
    chatbotId: string,
    params?: ListParams
  ): Promise<Paginated<any>> {
    const queryBase = buildListQuery(params);
    const query = queryBase
      ? `${queryBase}&chatbotId=${encodeURIComponent(chatbotId)}`
      : `chatbotId=${encodeURIComponent(chatbotId)}`;

    const url = `/core/conversation-history?${query}`;

    const result = await apiClient.get<ApiResponse<Paginated<any>>>(url, {
      authenticate: true,
    });

    return (result as any)?.data ?? (result as unknown as Paginated<any>);
  }
  ,

  async singleConversationHistoryById(
    id: string,
    params?: ListParams
  ): Promise<Paginated<any>> {
    const query = buildListQuery(params);
    const path = `/core/conversation-history/${encodeURIComponent(id)}`;
    const url = query ? `${path}?${query}` : path;

    const result = await apiClient.get<ApiResponse<Paginated<any>>>(url, {
      authenticate: true,
    });

    return (result as any).data ?? (result as unknown as Paginated<any>);
  },
};
